import { useRef, useState } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { addDays, MANUAL_SOURCES, newId } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { useAssets } from '../data/fleet.ts'
import { usePlan, type PlanJob } from '../data/plan.ts'
import { longDay, useRoster, type RosterPerson } from '../data/planUi.ts'
import { OPEN_WORK_ORDER_STATES, useWorkOrders } from '../data/safety.ts'
import { formatDuration, orderLabel } from '../data/work.ts'
import { spanOf, spanProblem } from '../data/workTime.ts'
import { localDate } from '../lib/time.ts'
import { BackLink, Banner, Field } from '../ui/components.tsx'
import { useAction, useWriteAccess } from '../ui/hooks.ts'
import { useNow } from '../ui/useNow.ts'

interface Choice { id: string; label: string; kind: 'job' | 'work_order' }

/** Time that was not timed (GK-016): a forgotten timer, a flat phone. A FACT, so it is written offline like a timer; the server always files it as waiting for the head
 *  greenkeeper's decision. The form only mirrors the server's plainest refusals (the end after the start, in the past, at most 24 hours); the server judges again. */
export function WorkManualPage() {
  const { t, i18n } = useTranslation()
  const { site, can } = useClub()
  const search = useSearch({ strict: false }) as { job?: string }
  const lang = i18n.language
  const { ready, plan } = usePlan(lang)
  const orders = useWorkOrders(); const assets = useAssets(); const roster = useRoster()
  if (!ready || !orders || !assets || !roster) return <p>{t('common.loading')}</p>
  const today = localDate(new Date(), site.timezone)
  const open = (j: PlanJob) => j.state !== 'done' && j.state !== 'cancelled'
  const jobs = [...plan.day(today).filter(open), ...plan.day(addDays(today, -1)).filter(open)]
  const wanted = search.job ? plan.byId.get(search.job) : undefined
  if (wanted && !jobs.some((j) => j.id === wanted.id)) jobs.unshift(wanted)         // a link from a job card always finds its job
  const choices: Choice[] = jobs.map((j) => ({ id: j.id, kind: 'job', label: j.scheduled_local_date === today ? j.title_text : `${j.title_text} · ${longDay(j.scheduled_local_date, lang)}` }))
  const orderChoices: Choice[] = can('workorder.labour') ? orders.filter((o) => OPEN_WORK_ORDER_STATES.includes(o.state)).map((o) => ({ id: o.id, kind: 'work_order', label: orderLabel(o, assets, t) })) : []
  return <ManualForm key={wanted?.id ?? 'none'} today={today} tz={site.timezone} lang={lang} choices={choices} orderChoices={orderChoices} roster={roster} first={wanted?.id ?? ''} />
}

function ManualForm({ today, tz, lang, choices, orderChoices, roster, first }: { today: string; tz: string; lang: string; choices: Choice[]; orderChoices: Choice[]; roster: RosterPerson[]; first: string }) {
  const { t } = useTranslation()
  const { send, can, personId, kiosk } = useClub()
  const access = useWriteAccess('worklog.record')
  const now = useNow(30_000)
  const { busy, error, run } = useAction()
  const ids = useRef({ session: newId(), interval: newId() })
  const [target, setTarget] = useState(first)
  const [day, setDay] = useState(today)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [source, setSource] = useState<(typeof MANUAL_SOURCES)[number]>('manual')
  const [note, setNote] = useState('')
  const [forPerson, setForPerson] = useState('')
  const [saved, setSaved] = useState(false)
  const head = can('worklog.review')
  const me = kiosk?.session?.personId ?? personId
  const choice = [...choices, ...orderChoices].find((c) => c.id === target)
  const span = spanOf(day, from, to, tz)
  const problem = spanProblem(span, now)
  const shown = problem && problem !== 'missing' ? t(`workManual.problem.${problem}`) : null
  const submit = () => run(async () => {
    if (!choice || !span || problem) return
    await send('work.manual_entry@1', {
      session_id: ids.current.session, interval_id: ids.current.interval,
      ...(choice.kind === 'job' ? { job_id: choice.id } : { work_order_id: choice.id }),
      ...(head && forPerson && forPerson !== me ? { person_id: forPerson } : {}),
      started_at: span.startedAt, ended_at: span.endedAt, source, note: note.trim() || null,
    })
    setSaved(true)
  })
  const again = () => { ids.current = { session: newId(), interval: newId() }; setFrom(''); setTo(''); setNote(''); setSaved(false) }
  if (saved) {
    return (
      <section className="flex flex-col gap-4" data-testid="manual-done">
        <Banner tone="ok"><span data-testid="manual-saved">{t('workManual.saved')}</span></Banner>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Link to="/" className="btn btn-primary" data-testid="manual-back">{t('common.today')}</Link>
          <Link to="/time" className="btn" data-testid="manual-mytime">{t('workManual.seeMyTime')}</Link>
          <button className="btn" onClick={again} data-testid="manual-another">{t('workManual.another')}</button>
        </div>
      </section>
    )
  }
  return (
    <form className="flex flex-col gap-4" data-testid="manual-page" onSubmit={(e) => { e.preventDefault(); void submit() }}>
      <BackLink to="/time">← {t('workManual.seeMyTime')}</BackLink>
      <h1 className="text-2xl font-bold">{t('workManual.title')}</h1>
      <p className="meta">{t('workManual.intro')}</p>
      {!access.permitted && <Banner tone="warn">{t('workManual.notAllowed')}</Banner>}
      <Field label={t('workManual.what')} htmlFor="manual-job" hint={choices.length + orderChoices.length === 0 ? t('workManual.noJobs') : undefined}>
        <select id="manual-job" className="input" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="manual-job">
          <option value="" disabled>{t('workManual.choose')}</option>
          {choices.length > 0 && <optgroup label={t('workManual.groupJobs')}>{choices.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}</optgroup>}
          {orderChoices.length > 0 && <optgroup label={t('workManual.groupOrders')}>{orderChoices.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}</optgroup>}
        </select>
      </Field>
      {head && (
        <Field label={t('workManual.forPerson')} htmlFor="manual-for">
          <select id="manual-for" className="input" value={forPerson} onChange={(e) => setForPerson(e.target.value)} data-testid="manual-for">
            <option value="">{t('workManual.me')}</option>
            {roster.filter((p) => p.id !== me).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
      )}
      <Field label={t('workManual.day')} htmlFor="manual-date" hint={t('workManual.dayHelp')}>
        <input id="manual-date" type="date" className="input" value={day} max={today} onChange={(e) => setDay(e.target.value)} data-testid="manual-date" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('workManual.from')} htmlFor="manual-from"><input id="manual-from" type="time" className="input" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="manual-from" /></Field>
        <Field label={t('workManual.to')} htmlFor="manual-to"><input id="manual-to" type="time" className="input" value={to} onChange={(e) => setTo(e.target.value)} data-testid="manual-to" /></Field>
      </div>
      {span?.nextDay && !problem && <p className="text-sm font-medium" data-testid="manual-nextday">{t('workManual.nextDay', { day: longDay(addDays(day, 1), lang) })}</p>}
      {shown && <p role="alert" className="text-sm font-medium text-red-700" data-testid="manual-problem">{shown}</p>}
      {span && !problem && <p className="text-lg font-semibold" data-testid="manual-duration">{t('workManual.duration', { time: formatDuration(span.ms) })}</p>}
      <Field label={t('workManual.sourceLabel')} htmlFor="manual-source">
        <select id="manual-source" className="input" value={source} onChange={(e) => setSource(e.target.value as (typeof MANUAL_SOURCES)[number])} data-testid="manual-source">
          {MANUAL_SOURCES.map((s) => <option key={s} value={s}>{t(`workManual.source.${s}`)}</option>)}
        </select>
      </Field>
      <Field label={t('workManual.note')} htmlFor="manual-note" hint={t('workManual.noteHelp')}>
        <textarea id="manual-note" className="input min-h-24" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} data-testid="manual-note" />
      </Field>
      <button type="submit" className="btn btn-primary text-xl" disabled={busy || !access.allowed || !choice || !span || !!problem} data-testid="manual-go">{busy ? t('common.saving') : t('workManual.go')}</button>
      {error && <Banner tone="error">{error}</Banner>}
    </form>
  )
}
